import {
   Alert02Icon,
   Archive03Icon,
   Briefcase01Icon,
   PinIcon,
   UndoIcon,
} from "@hugeicons/core-free-icons"
import { HugeiconsIcon } from "@hugeicons/react"
import { useSuspenseQuery } from "@tanstack/react-query"
import { createFileRoute, Link } from "@tanstack/react-router"
import { useVirtualizer } from "@tanstack/react-virtual"
import { formatCurrency } from "@unfiddle/core/currency"
import { makeShortId } from "@unfiddle/core/id"
import {
   CRM_ORDER_URL_PREFIX,
   ORDER_STATUSES,
   ORDER_STATUSES_TRANSLATION,
} from "@unfiddle/core/order/constants"
import { orderFilterSchema } from "@unfiddle/core/order/filter"
import {
   formatOrderDate,
   orderStatusGradient,
} from "@unfiddle/core/order/utils"
import type { RouterOutput } from "@unfiddle/core/trpc/types"
import {
   AvatarStack,
   AvatarStackItem,
} from "@unfiddle/ui/components/avatar-stack"
import { Badge } from "@unfiddle/ui/components/badge"
import { Button } from "@unfiddle/ui/components/button"
import {
   Combobox,
   ComboboxInput,
   ComboboxItem,
   ComboboxPopup,
   ComboboxTrigger,
} from "@unfiddle/ui/components/combobox"
import { Icons } from "@unfiddle/ui/components/icons"
import { MenuSeparator } from "@unfiddle/ui/components/menu"
import {
   ContextMenu,
   ContextMenuCheckboxItem,
   ContextMenuItem,
   ContextMenuPopup,
   ContextMenuTrigger,
} from "@unfiddle/ui/components/menu/context"
import {
   Tooltip,
   TooltipPopup,
   TooltipTrigger,
} from "@unfiddle/ui/components/tooltip"
import { getDefaultStore, useSetAtom } from "jotai"
import { useTheme } from "next-themes"
import * as React from "react"
import { useAuth } from "@/auth/hooks"
import { ClientSeverityIcon } from "@/client/components/client-severity-icon"
import { useForceUpdate } from "@/interactions/use-force-update"
import {
   Header,
   HeaderTitle,
   HeaderUserMenu,
   HeaderWorkspaceMenu,
} from "@/layout/components/header"
import { MainScrollArea } from "@/layout/components/main"
import { VList, VListContent } from "@/layout/components/vlist"
import { useCreateOrderAssignee } from "@/order/assignee/mutations/use-create-order-assignee"
import { useDeleteOrderAssignee } from "@/order/assignee/mutations/use-delete-order-assignee"
import { ArchiveOrderAlert } from "@/order/components/archive-order-alert"
import { OrderSeverityIcon } from "@/order/components/order-severity-icon"
import { DeleteOrderAlert } from "@/order/delete/delete-order-alert"
import { useDeleteOrder } from "@/order/delete/use-delete-order"
import { useUnreadOrders } from "@/order/message/read/queries"
import { useOrderQueryOptions } from "@/order/queries"
import { createOrderOpenAtom, orderSortAtom } from "@/order/store"
import { UpdateOrder } from "@/order/update/update-order"
import { useUpdateOrder } from "@/order/update/use-update-order"
import { DateFilter } from "@/routes/_authed/$workspaceId/_layout/(home)/-components/date-filter"
import { DisplayPopover } from "@/routes/_authed/$workspaceId/_layout/(home)/-components/display-popover"
import { HomeEmpty } from "@/routes/_authed/$workspaceId/_layout/(home)/-components/empty"
import { FilterMenu } from "@/routes/_authed/$workspaceId/_layout/(home)/-components/filter-menu"
import { Search } from "@/routes/_authed/$workspaceId/_layout/(home)/-components/search"
import { ToggleArchived } from "@/routes/_authed/$workspaceId/_layout/(home)/-components/toggle-archived"
import { trpc } from "@/trpc"
import { SuspenseBoundary } from "@/ui/components/suspense-boundary"
import { UserAvatar } from "@/user/components/user-avatar"
import { validator } from "@/validator"

export const Route = createFileRoute("/_authed/$workspaceId/_layout/(home)/")({
   component: RouteComponent,
   loaderDeps: (opts) => ({ search: opts.search }),
   loader: async (opts) => {
      opts.context.queryClient.prefetchQuery(
         trpc.order.list.queryOptions({
            workspaceId: opts.params.workspaceId,
            filter: {
               ...opts.deps.search,
               ...getDefaultStore().get(orderSortAtom),
            },
         }),
      )
   },
   validateSearch: validator(orderFilterSchema),
})

function RouteComponent() {
   const auth = useAuth()
   const scrollAreaRef = React.useRef<HTMLDivElement>(null)

   // if (auth.user.email !== "vasylpolishchuk22@gmail.com")
   // return (
   //    <div className="absolute inset-0 m-auto size-fit text-center">
   //       <h1>Технічна перерва</h1>
   //       <p>Сайт скоро повернеться</p>
   //    </div>
   // )

   const setCreateOrderOpen = useSetAtom(createOrderOpenAtom)

   return (
      <>
         <Header>
            <HeaderWorkspaceMenu />
            <HeaderTitle className="line-clamp-1">
               {auth.workspace.name}
            </HeaderTitle>
            <HeaderUserMenu />
         </Header>
         <MainScrollArea
            className="pt-0 lg:pt-0"
            container={false}
            ref={scrollAreaRef}
         >
            <Button
               onClick={() => setCreateOrderOpen(true)}
               className="fixed right-3 bottom-[calc(var(--bottom-navigation-height)+0.75rem)] z-10 overflow-visible shadow-xl md:right-8 md:bottom-8 md:h-9 md:px-3"
            >
               <HugeiconsIcon icon={Icons.plus} />
               Замовлення
            </Button>
            <div className="sticky top-0 z-5 flex min-h-12 items-center gap-1 border-surface-12/13 border-b bg-background px-1.5 shadow-xs/4 lg:min-h-10">
               <ToggleArchived />
               <DateFilter />
               <DisplayPopover />
               <FilterMenu />
               <Search />
            </div>
            <SuspenseBoundary>
               <Content scrollAreaRef={scrollAreaRef} />
            </SuspenseBoundary>
         </MainScrollArea>
      </>
   )
}

function Content({
   scrollAreaRef,
}: {
   scrollAreaRef: React.RefObject<HTMLDivElement | null>
}) {
   "use no memo"

   const queryOptions = useOrderQueryOptions()
   const query = useSuspenseQuery(queryOptions.list)
   const virtualizer = useVirtualizer({
      count: query.data.length,
      getScrollElement: () => scrollAreaRef.current,
      estimateSize: () => {
         return window.innerWidth < 1024 ? 72 : 44
      },
      overscan: 6,
   })
   const data = virtualizer.getVirtualItems()
   useForceUpdate()

   if (data.length === 0) return <HomeEmpty />

   return (
      <VList
         className="relative mb-20 w-full"
         totalSize={virtualizer.getTotalSize()}
      >
         <VListContent
            className="border-surface-5 border-b"
            start={data[0]?.start ?? 0}
         >
            {data.map((row) => {
               const order = query.data[row.index]
               if (!order) return null
               return (
                  <OrderRow
                     key={order.id}
                     order={order}
                  />
               )
            })}
         </VListContent>
      </VList>
   )
}

function _OrderRow({
   order,
}: {
   order: RouterOutput["order"]["list"][number]
}) {
   const params = Route.useParams()
   const auth = useAuth()
   const theme = useTheme()
   const [from, to] = orderStatusGradient(
      order.status ?? "canceled",
      theme.resolvedTheme ?? "light",
   )
   const update = useUpdateOrder()
   const deleteItem = useDeleteOrder()

   const [updateOpen, setUpdateOpen] = React.useState(false)
   const [archiveAlertOpen, setArchiveAlertOpen] = React.useState(false)
   const [deleteAlertOpen, setDeleteAlertOpen] = React.useState(false)
   const menuTriggerRef = React.useRef<HTMLDivElement>(null)

   const totalProfit = order.procurements.reduce(
      (acc, p) =>
         acc + ((order.sellingPrice ?? 0) - p.purchasePrice) * p.quantity,
      0,
   )
   const hasCrmUrl = order.crmUrl.includes(CRM_ORDER_URL_PREFIX)
   const assigned = order.assignees.some((a) => a.user.id === auth.user.id)
   const createAssignee = useCreateOrderAssignee()
   const deleteAssignee = useDeleteOrderAssignee()

   const unread = useUnreadOrders()

   const showStatus = order.status && order.status !== "pending"

   return (
      <ContextMenu>
         <ContextMenuTrigger
            className={
               "border-surface-5 border-t transition-colors duration-50 first:border-0 hover:bg-surface-1 data-popup-open:bg-surface-2"
            }
            ref={menuTriggerRef}
         >
            <Link
               to="/$workspaceId/order/$orderId"
               params={{
                  workspaceId: params.workspaceId,
                  orderId: order.id,
               }}
               className="relative grid h-18 grid-cols-[1fr_auto] grid-rows-[1fr_auto] items-center gap-x-2.5 gap-y-2 px-2.5 py-2 text-left lg:flex lg:h-11"
            >
               <div className="flex items-center gap-2 max-lg:w-full">
                  <OrderSeverityIcon
                     severity={order.severity}
                     className="mr-0.5 shrink-0"
                  />
                  <p className="whitespace-nowrap font-medium font-mono text-muted text-sm">
                     {makeShortId(order.shortId)}
                  </p>
                  <p className="flex items-center gap-1.5 font-medium text-sm lg:w-27.5">
                     <UserAvatar
                        size={25}
                        user={order.creator}
                        className="inline-block"
                     />
                     <span className="line-clamp-1">
                        {order.creator.name.split(" ")[0]}
                     </span>
                  </p>
               </div>
               <p
                  data-show-status={showStatus ? "" : undefined}
                  data-vat={order.paymentType === "llc_vat" ? "" : undefined}
                  className="col-span-2 col-start-1 row-start-2 mt-px line-clamp-1 break-normal font-semibold data-vat:text-orange-10 data-show-status:max-lg:mt-1.5 data-show-status:max-lg:max-w-[calc(100%-11rem)]"
               >
                  {order.name}
                  {unread.includes(order.id) ? (
                     <span className="motion-scale-in motion-duration-150 ml-2 inline-block size-2 rounded-full bg-red-9" />
                  ) : null}
               </p>
               <Tooltip>
                  <TooltipTrigger
                     delay={0}
                     render={
                        hasCrmUrl ? (
                           <span className="ml-auto grid size-4.5 shrink-0 place-items-center rounded-full bg-green-9">
                              <HugeiconsIcon
                                 icon={Icons.check}
                                 strokeWidth={2.5}
                                 className="size-3.5 shrink-0 text-white"
                              />
                           </span>
                        ) : (
                           <HugeiconsIcon
                              icon={Alert02Icon}
                              className="ml-auto size-4.5 shrink-0 fill-current text-red-9 [&>path+path]:stroke-[2.5] [&>path+path]:stroke-background"
                           />
                        )
                     }
                  />
                  <TooltipPopup>
                     {hasCrmUrl
                        ? "Замовлення створене в CRM"
                        : "Немає посилання на замовлення в CRM"}
                  </TooltipPopup>
               </Tooltip>
               {order.client ? (
                  <Tooltip>
                     <TooltipTrigger
                        delay={0}
                        render={
                           <Badge className="gap-1.5 max-lg:hidden">
                              <HugeiconsIcon
                                 icon={Briefcase01Icon}
                                 className="size-4.5"
                              />
                              <ClientSeverityIcon
                                 severity={order.client.severity}
                              />
                           </Badge>
                        }
                     />
                     <TooltipPopup>{order.client.name}</TooltipPopup>
                  </Tooltip>
               ) : null}
               <div
                  className="flex items-center gap-4"
                  data-has-client={order.client ? "" : undefined}
               >
                  {order.assignees.length === 0 ? null : (
                     <AvatarStack
                        size={25}
                        className="max-lg:hidden"
                     >
                        {order.assignees.map((assignee) => (
                           <AvatarStackItem key={assignee.user.id}>
                              <Tooltip>
                                 <TooltipTrigger
                                    delay={0}
                                    render={
                                       <UserAvatar
                                          size={25}
                                          user={assignee.user}
                                       />
                                    }
                                 />
                                 <TooltipPopup>
                                    {assignee.user.name}
                                 </TooltipPopup>
                              </Tooltip>
                           </AvatarStackItem>
                        ))}
                     </AvatarStack>
                  )}
                  {showStatus && ORDER_STATUSES_TRANSLATION[order.status] ? (
                     <Combobox
                        canBeEmpty
                        value={order.status}
                        onValueChange={(status) => {
                           if (order.status === status)
                              return update.mutate({
                                 orderId: order.id,
                                 workspaceId: params.workspaceId,
                                 status: "pending",
                              })

                           update.mutate({
                              orderId: order.id,
                              workspaceId: params.workspaceId,
                              status: status as never,
                           })
                        }}
                     >
                        <ComboboxTrigger
                           onClick={(e) => {
                              e.preventDefault()
                              e.stopPropagation()
                           }}
                           className={
                              "right-2 bottom-2 cursor-pointer max-lg:absolute"
                           }
                        >
                           <Badge
                              style={{
                                 background: `linear-gradient(140deg, ${from}, ${to})`,
                              }}
                           >
                              {ORDER_STATUSES_TRANSLATION[order.status] ??
                                 "Без статусу"}{" "}
                              {order.status === "successful"
                                 ? `(${formatCurrency(totalProfit, {
                                      currency: order.currency,
                                   })})`
                                 : ""}
                           </Badge>
                        </ComboboxTrigger>
                        <ComboboxPopup
                           sideOffset={4}
                           align="end"
                           onClick={(e) => {
                              e.preventDefault()
                              e.stopPropagation()
                           }}
                        >
                           <ComboboxInput />
                           {ORDER_STATUSES.map((s) =>
                              s === "pending" ? null : (
                                 <ComboboxItem
                                    key={s}
                                    value={s}
                                    keywords={[ORDER_STATUSES_TRANSLATION[s]]}
                                 >
                                    {ORDER_STATUSES_TRANSLATION[s]}
                                 </ComboboxItem>
                              ),
                           )}
                        </ComboboxPopup>
                     </Combobox>
                  ) : null}
               </div>
               <p className="min-w-15 text-muted max-lg:hidden">
                  {formatOrderDate(order.createdAt)}
               </p>
               <div
                  className="absolute"
                  onClick={(e) => {
                     e.preventDefault()
                     e.stopPropagation()
                  }}
               >
                  <UpdateOrder
                     open={updateOpen}
                     setOpen={setUpdateOpen}
                     orderId={order.id}
                     finalFocus={menuTriggerRef}
                  />
                  <ArchiveOrderAlert
                     open={archiveAlertOpen}
                     onOpenChange={setArchiveAlertOpen}
                     orderName={order.name}
                     finalFocus={menuTriggerRef}
                     action={() =>
                        update.mutate({
                           orderId: order.id,
                           workspaceId: params.workspaceId,
                           deletedAt: new Date(),
                        })
                     }
                  />
                  <DeleteOrderAlert
                     open={deleteAlertOpen}
                     onOpenChange={setDeleteAlertOpen}
                     orderName={order.name}
                     finalFocus={menuTriggerRef}
                     action={() =>
                        deleteItem.mutate({
                           orderId: order.id,
                           workspaceId: params.workspaceId,
                        })
                     }
                  />
               </div>
            </Link>
         </ContextMenuTrigger>
         <ContextMenuPopup>
            <ContextMenuCheckboxItem
               closeOnClick
               onClick={() => {
                  if (assigned)
                     return deleteAssignee.mutate({
                        orderId: order.id,
                        userId: auth.user.id,
                        workspaceId: auth.workspace.id,
                     })

                  createAssignee.mutate({
                     orderId: order.id,
                     userId: auth.user.id,
                     workspaceId: auth.workspace.id,
                  })
               }}
            >
               {assigned ? (
                  <>
                     <HugeiconsIcon icon={UndoIcon} />
                     Залишити
                  </>
               ) : (
                  <>
                     <HugeiconsIcon icon={PinIcon} />
                     Зайняти
                  </>
               )}
            </ContextMenuCheckboxItem>
            <ContextMenuItem
               onClick={() => {
                  setUpdateOpen(true)
               }}
            >
               <HugeiconsIcon icon={Icons.pencil} />
               Редагувати
            </ContextMenuItem>
            <MenuSeparator />
            {order.deletedAt === null ? (
               <ContextMenuItem
                  destructive
                  onClick={() => setArchiveAlertOpen(true)}
               >
                  <HugeiconsIcon icon={Archive03Icon} />
                  Архівувати
               </ContextMenuItem>
            ) : (
               <ContextMenuItem
                  onClick={() =>
                     update.mutate({
                        orderId: order.id,
                        workspaceId: params.workspaceId,
                        deletedAt: null,
                     })
                  }
               >
                  <HugeiconsIcon icon={UndoIcon} />
                  Відновити
               </ContextMenuItem>
            )}
            {order.deletedAt &&
            (auth.workspace.role === "owner" ||
               auth.workspace.role === "admin") ? (
               <ContextMenuItem
                  destructive
                  onClick={() => setDeleteAlertOpen(true)}
               >
                  <HugeiconsIcon icon={Icons.trash} />
                  Видалити
               </ContextMenuItem>
            ) : null}
         </ContextMenuPopup>
      </ContextMenu>
   )
}

const OrderRow = React.memo(_OrderRow)
